import { Injectable, OnDestroy, Injector } from '@angular/core';
import { BehaviorSubject, timer, fromEvent, merge, Subscription, filter, throttleTime, firstValueFrom } from 'rxjs';
import { isAuthDeadError } from '../auth/auth-errors';

export interface SessionWarningData {
  show: boolean;
  remainingTime: number;
  isExpiring: boolean;
}

/**
 * Vigila la caducidad del token de Auth0 y gobierna el aviso de sesión (`sessionWarning$`):
 * lo muestra cuando quedan ≤ 5 minutos, lleva la cuenta atrás y, si el usuario no extiende,
 * cierra sesión; si el SDK devuelve un código terminal o un token ya caducado, delega la
 * recuperación limpia en `AuthService.handleSessionDead`.
 *
 * Cómo vigila (25-10-2026). Antes sondeaba el token cada 5 segundos durante toda la sesión
 * (12 veces por minuto: promesa del SDK, descodificar el JWT y una escritura en localStorage
 * que nadie leía). Ahora cada comprobación lee la expiración del JWT y programa la SIGUIENTE
 * justo para cuando se entre en la ventana de aviso, con un tope de 60 s entre comprobaciones
 * porque el SDK puede renovar el token por su cuenta (otra pestaña, renovación silenciosa) y
 * la expiración cambia sin avisar. El aviso sale en el mismo segundo que antes. Lo que ya
 * cubría el sondeo frecuente, lo sigue cubriendo la validación al despertar (volver a la
 * pestaña, recuperar el foco o la conexión): en segundo plano los temporizadores van
 * throttleados o parados, y es al volver cuando hay que mirar.
 *
 * La cuenta atrás y el aviso se calculan SIEMPRE desde el JWT. Hasta el 25-10-2026 había una
 * vía paralela por localStorage (`auth_expires_in` + `auth_token_received_time`, escritas en
 * cada token nuevo) que un parche de `main.ts` bloqueaba al leer: nunca se usó, y se ha
 * retirado junto con el parche, los toasts de un `refreshToken()` que nadie llamaba y un
 * `initialize()` vacío.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionManagerService implements OnDestroy {
  /** Aviso cuando quedan ≤ 5 minutos. */
  private static readonly WARNING_SECONDS = 300;
  /** Tope entre comprobaciones: el token puede renovarse por fuera y cambiar su expiración. */
  private static readonly MAX_CHECK_INTERVAL_MS = 60_000;
  /** Reintento tras un fallo transitorio del SDK (red, timeout) o mientras el aviso está suprimido. */
  private static readonly RETRY_MS = 5_000;

  private readonly sessionWarningSubject = new BehaviorSubject<SessionWarningData>({
    show: false,
    remainingTime: 0,
    isExpiring: false
  });

  private tokenCheckTimer?: Subscription;
  private countdownTimer?: Subscription;
  private wakeUpSubscription?: Subscription;

  // Tras pulsar "Extender sesión" no volver a mostrar la advertencia durante
  // este margen. Evita que, si la renovación no logró alargar el token (y solo
  // se recuperó el token en caché próximo a expirar), la advertencia reaparezca
  // de inmediato en la siguiente comprobación.
  private suppressWarningUntil = 0;
  private static readonly EXTEND_SUPPRESS_MS = 60_000;

  // Fallos consecutivos de getAccessTokenSilently, solo para contexto en los
  // logs. Un fallo SIN código terminal (red, timeout) NUNCA cierra sesión
  // desde aquí — expulsar por un corte de red dejaba fuera al usuario — y los
  // códigos terminales (sesión incapaz de emitir tokens) se manejan al PRIMER
  // fallo vía isAuthDeadError → handleSessionDead, sin esperar 3 strikes.
  private consecutiveTokenErrors = 0;

  public sessionWarning$ = this.sessionWarningSubject.asObservable();

  constructor(private readonly injector: Injector) {
    // Iniciar auth subscription de forma diferida para evitar dependencia circular
    Promise.resolve().then(() => this.initAuthSubscription());
  }

  private async initAuthSubscription(): Promise<void> {
    const { AuthService } = await import('../auth/auth.service');
    this.authServiceRef = this.injector.get(AuthService);

    this.authServiceRef.isAuthenticated$.subscribe((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        this.startTokenMonitoring();
        this.startWakeUpValidation();
      } else {
        this.stopAllTimers();
        this.stopWakeUpValidation();
        this.hideSessionWarning();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopAllTimers();
    this.stopWakeUpValidation();
  }

  /** Primera comprobación inmediata; las siguientes las programa cada comprobación. */
  private startTokenMonitoring(): void {
    this.scheduleCheck(0);
  }

  private scheduleCheck(delayMs: number): void {
    this.tokenCheckTimer?.unsubscribe();
    this.tokenCheckTimer = timer(delayMs).subscribe(() => void this.checkTokenExpiration());
  }

  /**
   * Chequeo INMEDIATO al "despertar" (volver a la pestaña, recuperar el foco
   * o la conexión). En pestañas de fondo el navegador throttlea los timers
   * (Chrome: 1 tick/min) y con el equipo suspendido no corren en absoluto,
   * así que ninguna comprobación programada detecta nada mientras el usuario
   * está fuera: al volver, la app se pintaba como autenticada con la sesión
   * muerta y moría en cascada al primer click. Validar en el instante de volver
   * dispara la recuperación limpia ANTES de que el usuario toque nada.
   */
  private startWakeUpValidation(): void {
    if (this.wakeUpSubscription || typeof document === 'undefined') return;
    this.wakeUpSubscription = merge(
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
      fromEvent(globalThis.window, 'focus'),
      fromEvent(globalThis.window, 'online')
    ).pipe(
      // Un despertar real dispara varios de estos eventos casi a la vez;
      // con una validación basta.
      throttleTime(5000)
    ).subscribe(() => void this.checkTokenExpiration());
  }

  private stopWakeUpValidation(): void {
    this.wakeUpSubscription?.unsubscribe();
    this.wakeUpSubscription = undefined;
  }

  /**
   * Lee la expiración del token, muestra el aviso si toca y programa la siguiente comprobación.
   */
  private async checkTokenExpiration(): Promise<void> {
    try {
      const auth = this.getAuthService();
      if (!auth) return;
      const token: string = await firstValueFrom(auth.accessToken$);
      if (!token) return;

      this.consecutiveTokenErrors = 0;
      const secondsUntilExpiration = this.secondsUntilExpiration(token);

      if (secondsUntilExpiration <= 0) {
        // La SDK ha devuelto un token ya caducado (no ha podido renovarlo):
        // sesión incapaz de emitir tokens → recuperación limpia.
        this.handleDeadSession();
        return;
      }

      if (secondsUntilExpiration <= SessionManagerService.WARNING_SECONDS) {
        if (this.sessionWarningSubject.value.show) {
          // Mientras se muestra el aviso manda la cuenta atrás; no se programa nada más.
          return;
        }
        if (Date.now() >= this.suppressWarningUntil) {
          this.showSessionWarning(secondsUntilExpiration);
          return;
        }
        // Suprimido tras «Extender sesión»: volver a mirar en breve.
        this.scheduleCheck(SessionManagerService.RETRY_MS);
        return;
      }

      // Siguiente comprobación justo al entrar en la ventana de aviso, y nunca más tarde del tope.
      const msUntilWarning = (secondsUntilExpiration - SessionManagerService.WARNING_SECONDS) * 1000;
      this.scheduleCheck(Math.max(1000, Math.min(SessionManagerService.MAX_CHECK_INTERVAL_MS, msUntilWarning)));

    } catch (error) {
      if (isAuthDeadError(error)) {
        // Código TERMINAL (invalid_grant, missing_refresh_token…): la sesión
        // ya no puede emitir tokens y cada reintento solo añade otro POST
        // condenado a /oauth/token y más segundos de app rota. Recuperación
        // al PRIMER fallo (antes: 3 strikes ≈ 15s de UI zombie y un logout
        // duro que además destruía la cookie SSO).
        this.handleDeadSession();
        return;
      }

      this.consecutiveTokenErrors++;
      console.error(
        `Error transitorio obteniendo token (fallo ${this.consecutiveTokenErrors} consecutivos):`,
        error
      );
      // Sin código terminal (red caída, timeout) NUNCA cerrar sesión desde
      // aquí: expulsar por un hipo de red deja fuera al usuario. Si la
      // sesión está muerta de verdad, el interceptor o el guard la detectarán
      // con código terminal en la siguiente interacción.
      this.scheduleCheck(SessionManagerService.RETRY_MS);
    }
  }

  /** Segundos hasta que caduque el token (negativo si ya caducó). */
  private secondsUntilExpiration(token: string): number {
    const expirationTime = this.decodeJwtPayload(token).exp * 1000;
    return Math.floor((expirationTime - Date.now()) / 1000);
  }

  /**
   * Decodifica el payload de un JWT
   */
  private decodeJwtPayload(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent(
      globalThis.window.atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.codePointAt(0)!.toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  }

  /**
   * Muestra la advertencia de sesión con cuenta regresiva hasta la caducidad real del token.
   */
  private showSessionWarning(realTimeRemaining: number): void {
    // Mientras dura el aviso no hay comprobaciones: la cuenta atrás decide.
    this.tokenCheckTimer?.unsubscribe();
    this.tokenCheckTimer = undefined;

    // Cuenta atrás anclada a un instante ABSOLUTO, no a número de ticks:
    // en pestañas de fondo el navegador throttlea los timers (1 tick/min),
    // y contando ticks la cuenta se desincronizaba del reloj real — el
    // cierre llegaba minutos u horas tarde de tiempo de pared.
    const deadline = Date.now() + realTimeRemaining * 1000;

    this.sessionWarningSubject.next({
      show: true,
      remainingTime: realTimeRemaining,
      isExpiring: true
    });

    this.countdownTimer = timer(0, 1000).subscribe(() => {
      const remainingTime = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

      this.sessionWarningSubject.next({
        show: true,
        remainingTime,
        isExpiring: true
      });

      if (remainingTime <= 0) {
        this.handleSessionTimeout();
      }
    });
  }

  /**
   * El usuario decidió extender la sesión
   */
  public extendSession(): void {
    this.hideSessionWarning();

    // Al pulsar "Extender sesión" el usuario quiere seguir dentro: cerrar el
    // modal y NO redirigir al login aunque la renovación tenga problemas.
    // Suprimir la advertencia un margen para no reabrir el modal de inmediato.
    this.suppressWarningUntil = Date.now() + SessionManagerService.EXTEND_SUPPRESS_MS;

    // Renovar el token FORZANDO un token nuevo (cacheMode: 'off'). Usar
    // accessToken$ aquí no basta: el token cacheado sigue vigente durante la
    // advertencia y la SDK lo devolvería sin renovar, así que la sesión no se
    // alargaría.
    const auth = this.getAuthService();
    if (!auth?.renewToken) {
      // Sin capacidad de renovar: mantener la sesión con el token actual en
      // lugar de expulsar al usuario. Si el token expira de verdad, la siguiente
      // comprobación (secondsUntilExpiration <= 0) hará la recuperación.
      this.restartMonitoring();
      return;
    }
    auth.renewToken().subscribe({
      next: (_token: string) => {
        // renewToken() ya cae al token en caché si la renovación forzada falla,
        // así que reiniciamos el monitoreo con la mejor expiración disponible
        // sin cerrar sesión.
        this.restartMonitoring();
      },
      error: () => {
        // No debería ocurrir (renewToken captura errores), pero por seguridad
        // mantenemos la sesión abierta en vez de forzar el login.
        this.restartMonitoring();
      }
    });
  }

  /**
   * El usuario decidió cerrar sesión
   */
  public closeSession(): void {
    this.hideSessionWarning();
    this.performLogout();
  }

  /**
   * La sesión ya no puede emitir tokens (código terminal de auth0-spa-js o
   * token irrecuperablemente caducado): parar el monitoreo y delegar en
   * AuthService.handleSessionDead — limpia la caché zombie y re-loguea
   * conservando la ruta, SIN destruir la cookie SSO (si sigue viva, el
   * usuario vuelve sin teclear credenciales).
   */
  private handleDeadSession(): void {
    this.stopAllTimers();
    this.hideSessionWarning();
    this.getAuthService()?.handleSessionDead?.();
  }

  /**
   * Se acabó el tiempo de la advertencia: el usuario fue avisado durante la
   * cuenta atrás y no extendió → logout DELIBERADO (política de inactividad).
   * Aquí sí se cierra la sesión completa, incluida la SSO.
   */
  private handleSessionTimeout(): void {
    this.hideSessionWarning();
    this.performLogout();
  }

  /**
   * Oculta la advertencia de sesión
   */
  private hideSessionWarning(): void {
    this.sessionWarningSubject.next({
      show: false,
      remainingTime: 0,
      isExpiring: false
    });

    this.countdownTimer?.unsubscribe();
    this.countdownTimer = undefined;
  }

  /**
   * Realiza el logout usando AuthService
   */
  private performLogout(): void {
    this.stopAllTimers();
    this.getAuthService()?.logout();
  }

  /**
   * Detiene todos los timers
   */
  private stopAllTimers(): void {
    this.tokenCheckTimer?.unsubscribe();
    this.tokenCheckTimer = undefined;
    this.countdownTimer?.unsubscribe();
    this.countdownTimer = undefined;
    this.consecutiveTokenErrors = 0;
  }

  /**
   * Referencia lazy a AuthService para evitar dependencia circular
   */
  private authServiceRef: any = null;
  private getAuthService(): any {
    return this.authServiceRef;
  }

  /**
   * Reinicia el monitoreo (tras renovar el token)
   */
  public restartMonitoring(): void {
    this.stopAllTimers();
    this.startTokenMonitoring();
  }
}
